import { useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useColors } from '../../hooks/useColors';
import theme from '../../common/theme';
import { useScale } from '../../hooks/useScale';

const isWeb = Platform.OS === 'web';

const DocketItemStyles = ({ isSelected = false } = {}) => {
  const { scale } = useScale();
  const { colors } = useColors();

  return useMemo(
    () =>
      StyleSheet.create({
        // Docket Header
        header: {
          paddingHorizontal: scale(14),
          paddingVertical: scale(12),
          marginVertical: scale(4),
          borderRadius: theme.radius.m,
        },
        trainingHeader: {
          paddingVertical: scale(8),
          borderTopLeftRadius: theme.radius.m,
          borderTopRightRadius: theme.radius.m,
          backgroundColor: colors.red,
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: scale(4),
        },
        headerSpacingForTraining: {
          marginBottom: scale(4),
          marginTop: scale(0),
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
        headerSettings: {
          marginVertical: scale(4),
          borderRadius: theme.radius.m,
        },
        headerSelected: {
          backgroundColor: colors.greenLight,
          borderColor: colors.positive,
          borderWidth: scale(2),
        },
        headerContent: {
          flexDirection: 'row',
          justifyContent: 'space-between',
        },
        rowGroup: {
          paddingHorizontal: scale(14),
          flex: 1,
          alignSelf: 'center',
        },
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        headerTitle: {
          flex: 1,
        },
        headerOrderNumber: {
          flexShrink: 0,
          marginLeft: scale(8),
        },
        btnContainer: {
          paddingVertical: scale(24),
          justifyContent: 'center',
          alignItems: 'center',
          width: scale(38),
          backgroundColor: isSelected ? undefined : colors.transparent20,
        },
        headerCheckbox: {
          color: colors.positive,
        },
        text: {
          color: isSelected ? colors.green : colors.white,
          letterSpacing: -0.4,
          fontSize: scale(16),
          lineHeight: scale(22),
          fontFamily: theme.fonts.medium,
          fontVariant: ['tabular-nums'],
        },
        trainingText: {
          fontSize: scale(16),
        },
        seat: {
          color: colors.green,
          lineHeight: scale(22),
          fontFamily: theme.fonts.medium,
          fontVariant: ['tabular-nums'],
        },
        // Product
        product: {
          padding: scale(6),
          marginBottom: scale(4),
          borderRadius: theme.radius.s,
        },
        productHighlighted: {
          backgroundColor: colors.text,
          borderRadius: theme.radius.s,
        },
        line: {
          flexDirection: 'row',
        },
        rightContainer: {
          flexDirection: 'row',
          alignItems: 'center',
          marginLeft: 'auto',
        },
        name: {
          flex: 1,
          color: colors.text,
          letterSpacing: -0.2,
          fontSize: scale(16),
          lineHeight: scale(20),
          paddingVertical: scale(6),
          fontFamily: theme.fonts.medium,
        },
        nameHighlighted: {
          flex: 1,
          color: colors.textInverted,
          letterSpacing: -0.2,
          fontSize: scale(16),
          lineHeight: scale(20),
          paddingVertical: scale(6),
          fontFamily: theme.fonts.medium,
        },
        qty: {
          height: scale(32),
          minWidth: scale(30),
          marginRight: scale(8),
          paddingHorizontal: scale(4),
          borderRadius: theme.radius.s,
          backgroundColor: colors.qtyView,
        },
        qtyHighlighted: {
          backgroundColor: colors.qtyViewInverted,
        },
        qtyText: {
          fontSize: scale(14),
          lineHeight: scale(14),
          color: colors.text,
          textAlign: 'center',
          fontFamily: theme.fonts.medium,
        },
        secondaryText: {
          color: colors.accent,
          fontSize: scale(13),
          lineHeight: scale(16),
          textTransform: 'uppercase',
          paddingVertical: scale(6),
          fontFamily: theme.fonts.medium,
        },
        comboItem: {
          fontSize: scale(16),
          marginTop: scale(4),
          lineHeight: scale(20),
          marginLeft: scale(38),
          color: colors.text,
          letterSpacing: -0.2,
          fontFamily: theme.fonts.medium,
        },
        modifierBase: {
          opacity: 0.6,
          fontSize: scale(16),
          marginTop: scale(4),
          lineHeight: scale(20),
          color: colors.text,
          letterSpacing: -0.2,
          fontFamily: theme.fonts.medium,
        },
        modifierSpacing: {
          marginLeft: scale(38),
        },
        modifierHighlighted: {
          color: colors.textInverted,
        },
        // Applied to a modifier that matches a configured modifier style so it
        // stands out from the default 0.6-dimmed modifier text.
        modifierStyled: {
          opacity: 1,
        },
        modifierItalic: {
          fontStyle: 'italic',
        },
        modifierBackgroundPill: {
          paddingHorizontal: scale(6),
          paddingVertical: scale(2),
          borderRadius: scale(4),
          overflow: 'hidden',
        },
        removeDefaultModifier: {
          textDecorationLine: 'line-through',
          color: colors.red,
        },
        removedModifier: {
          color: colors.red,
        },
        note: {
          letterSpacing: -0.2,
          fontSize: scale(16),
          lineHeight: scale(20),
          marginLeft: scale(38),
          marginBottom: scale(4),
          color: colors.noteText,
        },
        allergenBadge: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(6),
          paddingVertical: scale(3),
          paddingHorizontal: scale(8),
          borderRadius: scale(4),
          backgroundColor: colors.allergenBadgeView,
        },
        allergenBadgeCount: {
          fontSize: scale(13),
          lineHeight: scale(16),
          fontFamily: theme.fonts.semibold,
          color: colors.allergenBadgeText,
        },
        allergenBanner: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(8),
          marginBottom: scale(8),
          paddingVertical: scale(6),
          paddingHorizontal: scale(6),
          borderRadius: scale(4),
          backgroundColor: colors.allergenBannerView,
        },
        allergenBannerText: {
          fontSize: scale(14),
          lineHeight: scale(20),
          fontFamily: theme.fonts.semibold,
          color: colors.allergenBannerText,
        },
        allergenItemLabel: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: scale(8),
          marginBottom: scale(4),
          paddingVertical: scale(9),
          paddingLeft: scale(6),
          borderRadius: scale(4),
          backgroundColor: colors.allergenItemView,
        },
        allergenItemLabelText: {
          flex: 1,
          fontSize: scale(14),
          lineHeight: scale(20),
          fontFamily: theme.fonts.semibold,
          color: colors.allergenItemText,
        },
        allergenItemLabelSeat: {
          lineHeight: scale(22),
          fontFamily: theme.fonts.medium,
          fontVariant: ['tabular-nums'],
          color: colors.allergenItemSeat,
        },
        allergenItemIconSpacer: {
          width: scale(40),
          // Cancels the row's columnGap so the seat lands on the same right
          // edge as the item row's, which has no gap before its icon.
          marginLeft: -scale(8),
        },
        // Icon
        icon: {
          width: scale(40),
          height: scale(36),
          marginLeft: 'auto',
          alignItems: 'center',
          justifyContent: 'center',
        },
        // Recalled, Due & Voided
        statusContainer: {
          marginVertical: scale(4),
          paddingVertical: scale(8),
          paddingHorizontal: scale(12),
          borderRadius: theme.radius.s,
        },
        statusText: {
          fontSize: scale(14),
          textAlign: 'center',
          paddingVertical: scale(4),
          fontFamily: theme.fonts.medium,
        },
        statusTextUppercase: {
          fontSize: scale(14),
          textAlign: 'center',
          paddingVertical: scale(4),
          fontFamily: theme.fonts.medium,
          textTransform: 'uppercase',
        },
        // Item Specific
        // View Styles
        dueContainer: {
          backgroundColor: colors.dueView,
        },
        noteContainer: {
          backgroundColor: colors.amber,
        },
        recalledContainer: {
          backgroundColor: colors.recalledView,
        },
        voidedContainer: {
          backgroundColor: colors.voidedView,
        },
        transferredContainer: {
          backgroundColor: colors.transferredView,
        },
        courseContainer: {
          backgroundColor: colors.courseView,
        },
        secondaryCourseContainer: {
          backgroundColor: undefined,
        },
        secondaryTextStyle: {
          fontSize: scale(14),
          paddingVertical: scale(4),
          fontFamily: theme.fonts.medium,
          color: colors.blue,
        },
        separatorContainer: {
          width: '100%',
          marginVertical: scale(8),
        },
        separatorLine: {
          opacity: 0.7,
        },
        // Text Styles
        preppedItem: {
          color: colors.grey6,
        },
        completedItem: {
          color: colors.completedText,
          textDecorationLine: 'line-through',
        },
        servedItem: {
          color: colors.green,
          textDecorationLine: 'line-through',
        },
        transferredItem: {
          color: colors.grey6,
          textDecorationLine: 'line-through',
        },
        voidedItem: {
          color: colors.red,
          textDecorationLine: 'line-through',
        },
        // Text Colours
        dueText: {
          color: colors.dueText,
        },
        courseText: {
          color: colors.courseText,
        },
        recallText: {
          color: colors.text,
        },
        recalledText: {
          color: colors.recalledText,
        },
        transferredText: {
          color: colors.transferredText,
        },
        voidedText: {
          color: colors.voidedText,
        },
        otherItemsContainer: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: scale(4),
          borderRadius: scale(4),
          backgroundColor: colors.elevation2,
          paddingVertical: scale(8),
          paddingHorizontal: scale(16),
        },
        otherItemsText: {
          flex: 1,
          zIndex: 1,
          color: colors.text,
          lineHeight: scale(24),
          fontSize: scale(14),
          fontFamily: theme.fonts.medium,
          fontVariant: ['tabular-nums'],
        },
        viewAllCoursesTextContainer: {
          flex: 1,
        },
        viewAllCoursesTitle: {
          color: colors.green,
          lineHeight: scale(24),
          fontSize: scale(14),
          fontFamily: theme.fonts.medium,
        },
        viewAllCoursesSubtitle: {
          opacity: 0.6,
          color: colors.textBody,
          fontSize: scale(12),
          marginTop: scale(2),
          fontFamily: theme.fonts.medium,
        },
        arrowText: {
          color: colors.text,
          fontSize: scale(14),
          fontFamily: theme.fonts.medium,
          fontVariant: ['tabular-nums'],
        },
        notifierProfile: {
          paddingHorizontal: scale(6),
          paddingTop: scale(4),
        },
        notifierSummaryStatus: {
          color: colors.grey6,
          fontSize: scale(12),
          fontFamily: theme.fonts.medium,
        },
        notifierSummaryStatusDone: {
          color: colors.green,
        },
        tagsContainer: {
          paddingTop: scale(4),
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: scale(4),
        },
        tagsRow: {
          flex: 1,
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: scale(4),
        },
        tagBadge: {
          backgroundColor: theme.colors.white,
          paddingVertical: scale(4),
          paddingHorizontal: scale(8),
          minWidth: scale(32),
          height: scale(24),
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: theme.radius.s,
        },
        tagLabel: {
          fontSize: scale(14),
        },
        rushTagBadge: {
          backgroundColor: colors.orange,
          width: scale(61),
        },
        rushTagLabel: {
          color: colors.white,
          fontSize: scale(13),
          fontFamily: theme.fonts.semibold,
        },
        docketCountBadge: {
          backgroundColor: colors.cyan,
          paddingVertical: scale(4),
          paddingHorizontal: scale(10),
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: theme.radius.s,
          borderWidth: 1,
          borderColor: colors.transparent20,
        },
        docketCountBadgeSelected: {
          backgroundColor: colors.orange,
        },
        docketCountLabel: {
          color: colors.white,
          fontSize: scale(14),
          fontFamily: theme.fonts.semibold,
        },
        viewAllCoursesCountBadge: {
          backgroundColor: colors.green,
          paddingVertical: scale(4),
          paddingHorizontal: scale(10),
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: theme.radius.s,
        },
        viewAllCoursesCountLabel: {
          color: colors.greenLight,
          opacity: 1,
          fontSize: scale(14),
          fontFamily: theme.fonts.semibold,
        },
      }),
    [colors, scale, isSelected],
  );
};

export default DocketItemStyles;
